import React, { useMemo, useReducer, useState } from "react";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import {
  PersonAddAltRounded,
  PersonRounded,
  LocationOnRounded,
  SchoolRounded,
  AccountBalanceRounded,
  DescriptionRounded,
  WorkRounded,
  LockRounded,
  CheckCircleRounded,
  ArrowBackRounded,
  ArrowForwardRounded,
  ShieldRounded,
  AddRounded,
  DeleteOutlineRounded,
} from "@mui/icons-material";
import { registerFDCByED } from "../services/edApi";

const steps = [
  { id: 1, title: "Personal & address", icon: PersonRounded },
  { id: 2, title: "Emergency & kin", icon: LocationOnRounded },
  { id: 3, title: "Education", icon: SchoolRounded },
  { id: 4, title: "Bank & documents", icon: AccountBalanceRounded },
  { id: 5, title: "Assignment", icon: WorkRounded },
  { id: 6, title: "Security", icon: LockRounded },
  { id: 7, title: "Review", icon: CheckCircleRounded },
];

const emptyEducation = () => ({
  level: "",
  institution: "",
  qualification: "",
  fieldOfStudy: "",
  startYear: "",
  endYear: "",
});

const initial = {
  firstName: "",
  middleName: "",
  surname: "",
  gender: "",
  dateOfBirth: "",
  maritalStatus: "",
  nationality: "Nigeria",
  phone: "",
  alternatePhone: "",
  email: "",
  nin: "",

  stateOfOrigin: "",
  lgaOfOrigin: "",
  hometown: "",
  originRegion: "",

  residentialCountry: "Nigeria",
  residentialState: "",
  city: "",
  localGovernment: "",
  area: "",
  street: "",
  postalCode: "",

  emergencyName: "",
  emergencyRelationship: "",
  emergencyGender: "",
  emergencyPhone: "",
  emergencyAlternatePhone: "",
  emergencyEmail: "",
  emergencyCountryOfOrigin: "Nigeria",
  emergencyStateOfOrigin: "",
  emergencyLgaOfOrigin: "",
  emergencyHometown: "",
  emergencyResidentialCountry: "Nigeria",
  emergencyResidentialState: "",
  emergencyResidentialLga: "",
  emergencyCity: "",
  emergencyArea: "",
  emergencyStreet: "",
  emergencyPostalCode: "",

  nextOfKinName: "",
  nextOfKinRelationship: "",
  nextOfKinGender: "",
  nextOfKinDateOfBirth: "",
  nextOfKinPhone: "",
  nextOfKinAlternatePhone: "",
  nextOfKinEmail: "",
  nextOfKinCountryOfOrigin: "Nigeria",
  nextOfKinStateOfOrigin: "",
  nextOfKinLgaOfOrigin: "",
  nextOfKinHometown: "",
  nextOfKinResidentialCountry: "Nigeria",
  nextOfKinResidentialState: "",
  nextOfKinResidentialLga: "",
  nextOfKinCity: "",
  nextOfKinArea: "",
  nextOfKinStreet: "",
  nextOfKinPostalCode: "",

  education: [emptyEducation()],

  bankName: "",
  accountName: "",
  accountNumber: "",
  bankCode: "",

  passportPhoto: null,
  cvFile: null,
  idType: "NIN_SLIP",
  idNumber: "",
  idDocument: null,
  additionalDocuments: [],

  assignedState: "",
  employmentType: "FULL_TIME",
  joinedAt: new Date().toISOString().slice(0, 10),
  rankName: "Field Development Consultant",
  rankLevel: "LEVEL-01",
  grade: "",

  password: "",
  confirmPassword: "",
  termsAccepted: false,
};

const fileInfo = (file) =>
  file
    ? {
        name: file.name,
        type: file.type || "application/octet-stream",
        size: file.size,
        lastModified: file.lastModified,
      }
    : null;

export default function EDFDCOnboarding() {
  const activeStaff = useSelector((s) => s?.universeStaffs?.activeStaff);
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useReducer((a, b) => ({ ...a, ...b }), initial);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const states = useMemo(
    () =>
      activeStaff?.territory?.states?.map((x) => x.stateName).filter(Boolean) ||
      activeStaff?.organization?.jurisdiction?.stateNames ||
      [],
    [activeStaff],
  );

  const schemas = {
    1: Yup.object({
      firstName: Yup.string().required("First name is required"),
      surname: Yup.string().required("Surname is required"),
      gender: Yup.string().required("Gender is required"),
      dateOfBirth: Yup.string().required("Date of birth is required"),
      phone: Yup.string().required("Phone is required"),
      email: Yup.string().email("Enter a valid email").required("Email is required"),
      nin: Yup.string().required("NIN is required"),
      stateOfOrigin: Yup.string().required("State of origin is required"),
      lgaOfOrigin: Yup.string().required("LGA of origin is required"),
      residentialState: Yup.string().required("Residential state is required"),
      city: Yup.string().required("City is required"),
    }),
    2: Yup.object({
      emergencyName: Yup.string().required("Emergency contact name is required"),
      emergencyRelationship: Yup.string().required("Relationship is required"),
      emergencyPhone: Yup.string().required("Emergency contact phone is required"),
      emergencyStateOfOrigin: Yup.string().required("Emergency contact state of origin is required"),
      emergencyResidentialState: Yup.string().required("Emergency contact residential state is required"),
      nextOfKinName: Yup.string().required("Next of kin name is required"),
      nextOfKinRelationship: Yup.string().required("Next of kin relationship is required"),
      nextOfKinPhone: Yup.string().required("Next of kin phone is required"),
      nextOfKinStateOfOrigin: Yup.string().required("Next of kin state of origin is required"),
    }),
    4: Yup.object({
      bankName: Yup.string().required("Bank name is required"),
      accountName: Yup.string().required("Account name is required"),
      accountNumber: Yup.string().required("Account number is required"),
      idType: Yup.string().required("Identification type is required"),
      idNumber: Yup.string().required("Identification number is required"),
    }),
    5: Yup.object({
      assignedState: Yup.string().required("Assign a state"),
      employmentType: Yup.string().required("Employment type is required"),
      joinedAt: Yup.string().required("Start date is required"),
    }),
    6: Yup.object({
      password: Yup.string().min(8, "Minimum 8 characters").required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords do not match")
        .required("Confirm the password"),
    }),
  };

  const validate = async () => {
    try {
      if (schemas[step]) await schemas[step].validate(data, { abortEarly: false });
      if (step === 3 && !data.education.some((x) => x.level && x.institution)) {
        setErrors({ education: "Add at least one education record." });
        return false;
      }
      if (step === 4 && (!data.passportPhoto || !data.cvFile || !data.idDocument)) {
        setErrors({
          documents: "Passport photograph, CV and identification document are required.",
        });
        return false;
      }
      setErrors({});
      return true;
    } catch (e) {
      const nextErrors = {};
      e.inner?.forEach((v) => {
        nextErrors[v.path] = v.message;
      });
      setErrors(nextErrors);
      return false;
    }
  };

  const next = async () => {
    if (await validate()) setStep((v) => Math.min(7, v + 1));
  };

  const updateEducation = (index, patch) => {
    const education = data.education.map((item, i) =>
      i === index ? { ...item, ...patch } : item,
    );
    setData({ education });
  };

  const addEducation = () => setData({ education: [...data.education, emptyEducation()] });
  const removeEducation = (index) => {
    if (data.education.length === 1) return;
    setData({ education: data.education.filter((_, i) => i !== index) });
  };

  const payload = () => ({
    staffData: {
      staffId: "",
      account: {
        status: "PENDING_APPROVAL",
        accessType: "STAFF_ACCESS",
        activated: false,
        activatedAt: null,
        suspendedAt: null,
        suspensionReason: null,
      },
      personalInfo: {
        firstName: data.firstName.trim(),
        middleName: data.middleName.trim(),
        surname: data.surname.trim(),
        gender: data.gender,
        dateOfBirth: data.dateOfBirth || null,
        maritalStatus: data.maritalStatus || "",
        nationality: data.nationality,
        phone: data.phone.trim(),
        alternatePhone: data.alternatePhone.trim(),
        email: data.email.trim().toLowerCase(),
        photo: "",
        nationalIdentification: {
          nin: data.nin.trim(),
        },
        origin: {
          country: data.nationality || "Nigeria",
          state: data.stateOfOrigin,
          localGovernment: data.lgaOfOrigin,
          hometown: data.hometown,
          region: data.originRegion,
        },
        address: {
          country: data.residentialCountry,
          state: data.residentialState,
          city: data.city,
          localGovernment: data.localGovernment,
          area: data.area,
          street: data.street,
          postalCode: data.postalCode,
        },
        emergencyContact: {
          name: data.emergencyName,
          relationship: data.emergencyRelationship,
          gender: data.emergencyGender,
          phone: data.emergencyPhone,
          alternatePhone: data.emergencyAlternatePhone,
          email: data.emergencyEmail.trim().toLowerCase(),
          origin: {
            country: data.emergencyCountryOfOrigin,
            state: data.emergencyStateOfOrigin,
            localGovernment: data.emergencyLgaOfOrigin,
            hometown: data.emergencyHometown,
          },
          address: {
            country: data.emergencyResidentialCountry,
            state: data.emergencyResidentialState,
            localGovernment: data.emergencyResidentialLga,
            city: data.emergencyCity,
            area: data.emergencyArea,
            street: data.emergencyStreet,
            postalCode: data.emergencyPostalCode,
          },
        },
        nextOfKin: {
          name: data.nextOfKinName,
          relationship: data.nextOfKinRelationship,
          gender: data.nextOfKinGender,
          dateOfBirth: data.nextOfKinDateOfBirth || null,
          phone: data.nextOfKinPhone,
          alternatePhone: data.nextOfKinAlternatePhone,
          email: data.nextOfKinEmail.trim().toLowerCase(),
          origin: {
            country: data.nextOfKinCountryOfOrigin,
            state: data.nextOfKinStateOfOrigin,
            localGovernment: data.nextOfKinLgaOfOrigin,
            hometown: data.nextOfKinHometown,
          },
          address: {
            country: data.nextOfKinResidentialCountry,
            state: data.nextOfKinResidentialState,
            localGovernment: data.nextOfKinResidentialLga,
            city: data.nextOfKinCity,
            area: data.nextOfKinArea,
            street: data.nextOfKinStreet,
            postalCode: data.nextOfKinPostalCode,
          },
        },
      },
      education: {
        history: data.education
          .filter((x) => x.level || x.institution)
          .map((x) => ({ ...x })),
      },
      banking: {
        salaryAccount: {
          bankName: data.bankName,
          bankCode: data.bankCode,
          accountName: data.accountName,
          accountNumber: data.accountNumber,
          currency: "NGN",
          verified: false,
          verifiedAt: null,
        },
      },
      documents: {
        passport: {
          ...fileInfo(data.passportPhoto),
          documentUrl: "",
          verified: false,
          verifiedAt: null,
        },
        cv: {
          ...fileInfo(data.cvFile),
          documentUrl: "",
          verified: false,
          verifiedAt: null,
        },
        identification: {
          type: data.idType,
          number: data.idNumber,
          ...fileInfo(data.idDocument),
          documentUrl: "",
          verified: false,
          verifiedAt: null,
        },
        profileDocuments: data.additionalDocuments.map((file) => ({
          category: "ADDITIONAL_DOCUMENT",
          ...fileInfo(file),
          documentUrl: "",
          verified: false,
          verifiedAt: null,
        })),
        employmentDocuments: [],
      },
      role: {
        roleId: "ROLE-FDC",
        name: "FDC",
        title: "Field Development Consultant",
        level: "FIELD",
        permissions: [],
      },
      referral: {
        code: "",
        active: false,
        statistics: {
          totalReferrals: 0,
          activeClients: 0,
          inactiveClients: 0,
          pendingClients: 0,
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      organization: {
        regionId: activeStaff?.organization?.regionId || activeStaff?.territory?.regionId || null,
        stateId: null,
        reportsTo: { staffId: null, role: "SDD" },
        jurisdiction: {
          regionId: activeStaff?.organization?.regionId || null,
          stateIds: [],
          stateNames: [data.assignedState],
        },
      },
      employment: {
        status: "PENDING_APPROVAL",
        employmentType: data.employmentType,
        joinedAt: data.joinedAt,
        confirmedAt: null,
        terminatedAt: null,
        terminationReason: null,
        rank: { name: data.rankName, level: data.rankLevel, grade: data.grade },
        allowances: { enabled: true, items: [] },
        commission: {
          enabled: true,
          clientRegistration: { amount: 3000, currency: "NGN" },
        },
      },
      territory: {
        regionId: activeStaff?.territory?.regionId || null,
        regionName: activeStaff?.territory?.regionName || "",
        states: [
          {
            stateId: null,
            stateName: data.assignedState,
            status: "PENDING_APPROVAL",
            assignedAt: new Date(),
          },
        ],
        totalStates: 1,
        assignedAt: new Date(),
        lastUpdatedAt: new Date(),
      },
      management: { clients: { total: 0, active: 0, inactive: 0, pending: 0 } },
      performance: {
        currentPeriod: { target: 0, achieved: 0, percentage: 0, clientsRegistered: 0, activeClients: 0 },
        lifetime: { clientsRegistered: 0, targetsAssigned: 0, targetsCompleted: 0 },
        lastCalculatedAt: null,
      },
      targets: { currentTargetId: null, assigned: 0, completed: 0, pending: 0, lastAssignedAt: null },
      wallet: {
        balance: 0,
        pendingBalance: 0,
        availableBalance: 0,
        totalEarned: 0,
        totalWithdrawn: 0,
        currency: "NGN",
        lastTransactionAt: null,
      },
      payroll: {
        salaryBalance: 0,
        allowanceBalance: 0,
        commissionBalance: 0,
        deductions: 0,
        taxes: 0,
        lastPaymentAt: null,
        nextPaymentAt: null,
        currency: "NGN",
      },
      auth: {
        credentials: { hashedPassword: data.password },
        authentication: {
          method: "EMAIL_PHONE_AND_PASSWORD",
          status: "PENDING_APPROVAL",
          lastLoginAt: null,
          failedLoginAttempts: 0,
          locked: true,
          lockedAt: null,
          lockReason: "Awaiting higher-authority approval",
        },
      },
      audit: {
        createdBy: activeStaff?.staffId || "ED",
        lastModifiedBy: "",
        approvedBy: null,
        approvedAt: null,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    },
    onboarding: {
      submittedBy: {
        staffId: activeStaff?.staffId,
        role: "ED",
        name: [activeStaff?.personalInfo?.firstName, activeStaff?.personalInfo?.surname]
          .filter(Boolean)
          .join(" "),
      },
      regionId: activeStaff?.organization?.regionId || activeStaff?.territory?.regionId,
      state: data.assignedState,
      approvalStatus: "PENDING_APPROVAL",
    },
  });

  const submit = async () => {
    if (!data.termsAccepted) {
      return enqueueSnackbar("Confirm the information before submitting.", { variant: "warning" });
    }
    try {
      setLoading(true);
      const r = await registerFDCByED(activeStaff?.staffId, payload());
      setSuccess(r);
      enqueueSnackbar(r?.message || "FDC registration submitted for approval.", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || e.message || "Unable to submit FDC registration.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="edPage">
        <section className="edOnboardSuccess">
          <CheckCircleRounded />
          <small>REGISTRATION SUBMITTED</small>
          <h2>FDC onboarding is awaiting approval</h2>
          <p>
            The Field Development Consultant has been registered successfully, but the account is not active yet.
            Higher authority must review and approve the registration before the FDC can sign in and begin work.
          </p>
          <div>
            <span>Submitted by</span>
            <strong>{activeStaff?.personalInfo?.firstName} {activeStaff?.personalInfo?.surname} · Executive Director</strong>
          </div>
          <button onClick={() => { setSuccess(null); setData(initial); setStep(1); }}>Register another FDC</button>
        </section>
      </div>
    );
  }

  return (
    <div className="edPage edOnboarding">
      <div className="edPageTitle">
        <div>
          <small>WORKFORCE ONBOARDING</small>
          <h2>Register Field Development Consultant</h2>
          <p>Create a complete FDC personnel application within {activeStaff?.territory?.regionName || "your region"}. Activation remains with higher authority.</p>
        </div>
        <span className="edPendingBadge"><ShieldRounded /> Pending-approval workflow</span>
      </div>

      <nav className="edOnboardSteps">
        {steps.map((s) => (
          <button key={s.id} className={`${step === s.id ? "active" : ""} ${step > s.id ? "done" : ""}`} onClick={() => s.id < step && setStep(s.id)}>
            <s.icon />
            <span><small>STEP {s.id}</small><strong>{s.title}</strong></span>
          </button>
        ))}
      </nav>

      <section className="edOnboardForm">
        {step === 1 && <>
          <Heading title="Personal information" text="Capture the consultant's legal identity, contact information, origin and current residential address." />
          <Grid>
            <Field label="First name" error={errors.firstName}><input value={data.firstName} onChange={(e) => setData({ firstName: e.target.value })} /></Field>
            <Field label="Middle name"><input value={data.middleName} onChange={(e) => setData({ middleName: e.target.value })} /></Field>
            <Field label="Surname" error={errors.surname}><input value={data.surname} onChange={(e) => setData({ surname: e.target.value })} /></Field>
            <Field label="Gender" error={errors.gender}><select value={data.gender} onChange={(e) => setData({ gender: e.target.value })}><option value="">Select</option><option>MALE</option><option>FEMALE</option></select></Field>
            <Field label="Date of birth" error={errors.dateOfBirth}><input type="date" value={data.dateOfBirth} onChange={(e) => setData({ dateOfBirth: e.target.value })} /></Field>
            <Field label="Marital status"><select value={data.maritalStatus} onChange={(e) => setData({ maritalStatus: e.target.value })}><option value="">Select</option><option>SINGLE</option><option>MARRIED</option><option>DIVORCED</option><option>WIDOWED</option></select></Field>
            <Field label="Nationality"><input value={data.nationality} onChange={(e) => setData({ nationality: e.target.value })} /></Field>
            <Field label="NIN" error={errors.nin}><input value={data.nin} onChange={(e) => setData({ nin: e.target.value })} placeholder="National Identification Number" /></Field>
            <Field label="Phone" error={errors.phone}><input value={data.phone} onChange={(e) => setData({ phone: e.target.value })} /></Field>
            <Field label="Alternate phone"><input value={data.alternatePhone} onChange={(e) => setData({ alternatePhone: e.target.value })} /></Field>
            <Field label="Email" error={errors.email}><input type="email" value={data.email} onChange={(e) => setData({ email: e.target.value })} /></Field>
          </Grid>

          <Heading title="Origin information" text="Record the consultant's state, local government and place of origin separately from the current address." />
          <Grid>
            <Field label="Country of origin"><input value={data.nationality} disabled /></Field>
            <Field label="State of origin" error={errors.stateOfOrigin}><input value={data.stateOfOrigin} onChange={(e) => setData({ stateOfOrigin: e.target.value })} /></Field>
            <Field label="LGA of origin" error={errors.lgaOfOrigin}><input value={data.lgaOfOrigin} onChange={(e) => setData({ lgaOfOrigin: e.target.value })} /></Field>
            <Field label="Hometown"><input value={data.hometown} onChange={(e) => setData({ hometown: e.target.value })} /></Field>
            <Field label="Region / geopolitical zone"><input value={data.originRegion} onChange={(e) => setData({ originRegion: e.target.value })} placeholder="e.g. North-West" /></Field>
          </Grid>

          <Heading title="Current residential address" text="Capture where the consultant currently lives. This remains separate from place of origin." />
          <Grid>
            <Field label="Country"><input value={data.residentialCountry} onChange={(e) => setData({ residentialCountry: e.target.value })} /></Field>
            <Field label="Residential state" error={errors.residentialState}><input value={data.residentialState} onChange={(e) => setData({ residentialState: e.target.value })} /></Field>
            <Field label="Residential city" error={errors.city}><input value={data.city} onChange={(e) => setData({ city: e.target.value })} /></Field>
            <Field label="Residential LGA"><input value={data.localGovernment} onChange={(e) => setData({ localGovernment: e.target.value })} /></Field>
            <Field label="Area"><input value={data.area} onChange={(e) => setData({ area: e.target.value })} /></Field>
            <Field label="Street"><input value={data.street} onChange={(e) => setData({ street: e.target.value })} /></Field>
            <Field label="Postal code"><input value={data.postalCode} onChange={(e) => setData({ postalCode: e.target.value })} /></Field>
          </Grid>
        </>}

        {step === 2 && <>
          <Heading title="Emergency contact" text="Provide a complete profile for the person Universe Inventory should contact in an emergency." />
          <Grid>
            <Field label="Full name" error={errors.emergencyName}><input value={data.emergencyName} onChange={(e) => setData({ emergencyName: e.target.value })} /></Field>
            <Field label="Relationship" error={errors.emergencyRelationship}><input value={data.emergencyRelationship} onChange={(e) => setData({ emergencyRelationship: e.target.value })} /></Field>
            <Field label="Gender"><select value={data.emergencyGender} onChange={(e) => setData({ emergencyGender: e.target.value })}><option value="">Select</option><option>MALE</option><option>FEMALE</option></select></Field>
            <Field label="Phone" error={errors.emergencyPhone}><input value={data.emergencyPhone} onChange={(e) => setData({ emergencyPhone: e.target.value })} /></Field>
            <Field label="Alternate phone"><input value={data.emergencyAlternatePhone} onChange={(e) => setData({ emergencyAlternatePhone: e.target.value })} /></Field>
            <Field label="Email"><input type="email" value={data.emergencyEmail} onChange={(e) => setData({ emergencyEmail: e.target.value })} /></Field>
          </Grid>
          <Heading title="Emergency contact origin" text="Record the emergency contact's origin information." />
          <Grid>
            <Field label="Country of origin"><input value={data.emergencyCountryOfOrigin} onChange={(e) => setData({ emergencyCountryOfOrigin: e.target.value })} /></Field>
            <Field label="State of origin" error={errors.emergencyStateOfOrigin}><input value={data.emergencyStateOfOrigin} onChange={(e) => setData({ emergencyStateOfOrigin: e.target.value })} /></Field>
            <Field label="LGA of origin"><input value={data.emergencyLgaOfOrigin} onChange={(e) => setData({ emergencyLgaOfOrigin: e.target.value })} /></Field>
            <Field label="Hometown"><input value={data.emergencyHometown} onChange={(e) => setData({ emergencyHometown: e.target.value })} /></Field>
          </Grid>
          <Heading title="Emergency contact address" text="Capture the emergency contact's current residential address." />
          <Grid>
            <Field label="Country"><input value={data.emergencyResidentialCountry} onChange={(e) => setData({ emergencyResidentialCountry: e.target.value })} /></Field>
            <Field label="State" error={errors.emergencyResidentialState}><input value={data.emergencyResidentialState} onChange={(e) => setData({ emergencyResidentialState: e.target.value })} /></Field>
            <Field label="LGA"><input value={data.emergencyResidentialLga} onChange={(e) => setData({ emergencyResidentialLga: e.target.value })} /></Field>
            <Field label="City"><input value={data.emergencyCity} onChange={(e) => setData({ emergencyCity: e.target.value })} /></Field>
            <Field label="Area"><input value={data.emergencyArea} onChange={(e) => setData({ emergencyArea: e.target.value })} /></Field>
            <Field label="Street / address"><input value={data.emergencyStreet} onChange={(e) => setData({ emergencyStreet: e.target.value })} /></Field>
            <Field label="Postal code"><input value={data.emergencyPostalCode} onChange={(e) => setData({ emergencyPostalCode: e.target.value })} /></Field>
          </Grid>

          <Heading title="Next of kin" text="Provide the consultant's designated next-of-kin information separately from the emergency contact." />
          <Grid>
            <Field label="Full name" error={errors.nextOfKinName}><input value={data.nextOfKinName} onChange={(e) => setData({ nextOfKinName: e.target.value })} /></Field>
            <Field label="Relationship" error={errors.nextOfKinRelationship}><input value={data.nextOfKinRelationship} onChange={(e) => setData({ nextOfKinRelationship: e.target.value })} /></Field>
            <Field label="Gender"><select value={data.nextOfKinGender} onChange={(e) => setData({ nextOfKinGender: e.target.value })}><option value="">Select</option><option>MALE</option><option>FEMALE</option></select></Field>
            <Field label="Date of birth"><input type="date" value={data.nextOfKinDateOfBirth} onChange={(e) => setData({ nextOfKinDateOfBirth: e.target.value })} /></Field>
            <Field label="Phone" error={errors.nextOfKinPhone}><input value={data.nextOfKinPhone} onChange={(e) => setData({ nextOfKinPhone: e.target.value })} /></Field>
            <Field label="Alternate phone"><input value={data.nextOfKinAlternatePhone} onChange={(e) => setData({ nextOfKinAlternatePhone: e.target.value })} /></Field>
            <Field label="Email"><input type="email" value={data.nextOfKinEmail} onChange={(e) => setData({ nextOfKinEmail: e.target.value })} /></Field>
          </Grid>
          <Heading title="Next of kin origin" text="Record the next of kin's place of origin." />
          <Grid>
            <Field label="Country of origin"><input value={data.nextOfKinCountryOfOrigin} onChange={(e) => setData({ nextOfKinCountryOfOrigin: e.target.value })} /></Field>
            <Field label="State of origin" error={errors.nextOfKinStateOfOrigin}><input value={data.nextOfKinStateOfOrigin} onChange={(e) => setData({ nextOfKinStateOfOrigin: e.target.value })} /></Field>
            <Field label="LGA of origin"><input value={data.nextOfKinLgaOfOrigin} onChange={(e) => setData({ nextOfKinLgaOfOrigin: e.target.value })} /></Field>
            <Field label="Hometown"><input value={data.nextOfKinHometown} onChange={(e) => setData({ nextOfKinHometown: e.target.value })} /></Field>
          </Grid>
          <Heading title="Next of kin address" text="Capture the next of kin's current residential address." />
          <Grid>
            <Field label="Country"><input value={data.nextOfKinResidentialCountry} onChange={(e) => setData({ nextOfKinResidentialCountry: e.target.value })} /></Field>
            <Field label="State"><input value={data.nextOfKinResidentialState} onChange={(e) => setData({ nextOfKinResidentialState: e.target.value })} /></Field>
            <Field label="LGA"><input value={data.nextOfKinResidentialLga} onChange={(e) => setData({ nextOfKinResidentialLga: e.target.value })} /></Field>
            <Field label="City"><input value={data.nextOfKinCity} onChange={(e) => setData({ nextOfKinCity: e.target.value })} /></Field>
            <Field label="Area"><input value={data.nextOfKinArea} onChange={(e) => setData({ nextOfKinArea: e.target.value })} /></Field>
            <Field label="Street / address"><input value={data.nextOfKinStreet} onChange={(e) => setData({ nextOfKinStreet: e.target.value })} /></Field>
            <Field label="Postal code"><input value={data.nextOfKinPostalCode} onChange={(e) => setData({ nextOfKinPostalCode: e.target.value })} /></Field>
          </Grid>
        </>}

        {step === 3 && <>
          <Heading title="Educational qualifications" text="Add primary, secondary, higher institution, university or other relevant qualifications." />
          {data.education.map((item, index) => (
            <div className="edAssignmentBanner" key={index}>
              <SchoolRounded />
              <div style={{ width: "100%" }}>
                <Grid>
                  <Field label="Education level"><select value={item.level} onChange={(e) => updateEducation(index, { level: e.target.value })}><option value="">Select</option><option>PRIMARY</option><option>SECONDARY</option><option>COLLEGE</option><option>POLYTECHNIC</option><option>UNIVERSITY</option><option>POSTGRADUATE</option><option>PROFESSIONAL</option><option>OTHER</option></select></Field>
                  <Field label="Institution"><input value={item.institution} onChange={(e) => updateEducation(index, { institution: e.target.value })} /></Field>
                  <Field label="Qualification / certificate"><input value={item.qualification} onChange={(e) => updateEducation(index, { qualification: e.target.value })} placeholder="e.g. B.Sc, HND, SSCE" /></Field>
                  <Field label="Field of study"><input value={item.fieldOfStudy} onChange={(e) => updateEducation(index, { fieldOfStudy: e.target.value })} /></Field>
                  <Field label="Start year"><input value={item.startYear} onChange={(e) => updateEducation(index, { startYear: e.target.value })} /></Field>
                  <Field label="End year"><input value={item.endYear} onChange={(e) => updateEducation(index, { endYear: e.target.value })} /></Field>
                </Grid>
                {data.education.length > 1 && <button type="button" onClick={() => removeEducation(index)}><DeleteOutlineRounded /> Remove qualification</button>}
              </div>
            </div>
          ))}
          {errors.education && <div className="edError">{errors.education}</div>}
          <button type="button" onClick={addEducation}><AddRounded /> Add another qualification</button>
        </>}

        {step === 4 && <>
          <Heading title="Salary bank account" text="Collect the FDC's salary destination account. This does not set or propose a salary amount." />
          <Grid>
            <Field label="Bank name" error={errors.bankName}>
              <NigeriaBankSelect
                value={data.bankName}
                bankCode={data.bankCode}
                onChange={(bank) => setData({ bankName: bank.name, bankCode: bank.code || "" })}
              />
            </Field>
            <Field label="Bank code"><input value={data.bankCode} readOnly placeholder="Filled automatically" /></Field>
            <Field label="Account name" error={errors.accountName}><input value={data.accountName} onChange={(e) => setData({ accountName: e.target.value })} /></Field>
            <Field label="Account number" error={errors.accountNumber}><input inputMode="numeric" value={data.accountNumber} onChange={(e) => setData({ accountNumber: e.target.value.replace(/\D/g, "") })} /></Field>
          </Grid>
          <Heading title="Documents" text="Attach the personnel documents required for review and verification." />
          <Grid>
            <Field label="Passport photograph"><input type="file" accept="image/*" onChange={(e) => setData({ passportPhoto: e.target.files?.[0] || null })} />{data.passportPhoto && <small>{data.passportPhoto.name}</small>}</Field>
            <Field label="Curriculum Vitae (CV)"><input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setData({ cvFile: e.target.files?.[0] || null })} />{data.cvFile && <small>{data.cvFile.name}</small>}</Field>
            <Field label="ID type" error={errors.idType}><select value={data.idType} onChange={(e) => setData({ idType: e.target.value })}><option>NIN_SLIP</option><option>NATIONAL_ID_CARD</option><option>VOTERS_CARD</option><option>DRIVERS_LICENSE</option><option>INTERNATIONAL_PASSPORT</option></select></Field>
            <Field label="ID number" error={errors.idNumber}><input value={data.idNumber} onChange={(e) => setData({ idNumber: e.target.value })} /></Field>
            <Field label="Identification document"><input type="file" accept="image/*,.pdf" onChange={(e) => setData({ idDocument: e.target.files?.[0] || null })} />{data.idDocument && <small>{data.idDocument.name}</small>}</Field>
            <Field label="Additional documents"><input type="file" multiple accept="image/*,.pdf,.doc,.docx" onChange={(e) => setData({ additionalDocuments: Array.from(e.target.files || []) })} />{data.additionalDocuments.length > 0 && <small>{data.additionalDocuments.length} file(s) selected</small>}</Field>
          </Grid>
          {errors.documents && <div className="edError">{errors.documents}</div>}
          <div className="edSecurityNotice"><DescriptionRounded /><div><strong>Document verification</strong><span>Documents remain unverified until reviewed by the authorized higher authority.</span></div></div>
        </>}

        {step === 5 && <>
          <Heading title="Regional & employment assignment" text="Assign the FDC inside the ED's jurisdiction. Compensation remains outside this workflow." />
          <div className="edAssignmentBanner"><PublicInfo activeStaff={activeStaff} /></div>
          <Grid>
            <Field label="Assigned state" error={errors.assignedState}><select value={data.assignedState} onChange={(e) => setData({ assignedState: e.target.value })}><option value="">Select state</option>{states.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Employment type"><select value={data.employmentType} onChange={(e) => setData({ employmentType: e.target.value })}><option>FULL_TIME</option><option>PART_TIME</option><option>CONTRACT</option></select></Field>
            <Field label="Start date"><input type="date" value={data.joinedAt} onChange={(e) => setData({ joinedAt: e.target.value })} /></Field>
            <Field label="Rank"><input value={data.rankName} disabled /></Field>
            <Field label="Level"><input value={data.rankLevel} onChange={(e) => setData({ rankLevel: e.target.value })} /></Field>
            <Field label="Grade"><input value={data.grade} onChange={(e) => setData({ grade: e.target.value })} /></Field>
          </Grid>
          <div className="edCommissionNotice"><strong>Client acquisition commission</strong><span>₦3,000 for a qualifying client personally referred/registered by this FDC. Salary is not assigned by the Executive Director in this registration.</span></div>
        </>}

        {step === 6 && <>
          <Heading title="Initial security" text="Create temporary credentials. The account remains locked until approval." />
          <div className="edSecurityNotice"><LockRounded /><div><strong>Approval lock is mandatory</strong><span>Submitting this form does not activate the FDC. Authentication remains PENDING_APPROVAL.</span></div></div>
          <Grid>
            <Field label="Password" error={errors.password}><input type="password" value={data.password} onChange={(e) => setData({ password: e.target.value })} /></Field>
            <Field label="Confirm password" error={errors.confirmPassword}><input type="password" value={data.confirmPassword} onChange={(e) => setData({ confirmPassword: e.target.value })} /></Field>
          </Grid>
        </>}

        {step === 7 && <>
          <Heading title="Review & submit" text="Review the complete personnel application before sending it for higher-authority approval." />
          <div className="edReviewSummary">
            <article><small>CONSULTANT</small><strong>{data.firstName} {data.middleName} {data.surname}</strong><span>{data.email} · {data.phone}</span></article>
            <article><small>IDENTITY</small><strong>NIN: {data.nin || "—"}</strong><span>{data.stateOfOrigin || "—"} · {data.lgaOfOrigin || "—"}</span></article>
            <article><small>EDUCATION</small><strong>{data.education.filter((x) => x.level || x.institution).length} qualification(s)</strong><span>{data.education.filter((x) => x.institution).map((x) => x.institution).join(", ") || "—"}</span></article>
            <article><small>SALARY ACCOUNT</small><strong>{data.bankName || "—"}</strong><span>{data.accountName || "—"} · {data.accountNumber || "—"}</span></article>
            <article><small>DOCUMENTS</small><strong>Passport · CV · ID</strong><span>{data.additionalDocuments.length} additional document(s)</span></article>
            <article><small>ASSIGNMENT</small><strong>{data.assignedState || "—"}</strong><span>{activeStaff?.territory?.regionName}</span></article>
            <article><small>ACCOUNT STATE</small><strong>PENDING APPROVAL</strong><span>Not permitted to sign in until approved</span></article>
            <article><small>SUBMITTED BY</small><strong>{activeStaff?.staffId}</strong><span>Executive Director</span></article>
          </div>
          <label className="edConfirm"><input type="checkbox" checked={data.termsAccepted} onChange={(e) => setData({ termsAccepted: e.target.checked })} /><span>I confirm that this information is correct and understand that this registration requires higher-authority approval.</span></label>
        </>}

        <footer className="edOnboardFooter">
          <button disabled={step === 1 || loading} onClick={() => setStep((v) => v - 1)}><ArrowBackRounded /> Back</button>
          {step < 7
            ? <button className="primary" onClick={next}>Continue <ArrowForwardRounded /></button>
            : <button className="primary" disabled={loading} onClick={submit}><PersonAddAltRounded />{loading ? "Submitting..." : "Submit FDC for approval"}</button>}
        </footer>
      </section>
    </div>
  );
}

const NIGERIAN_BANKS_API =
  "https://cdn.jsdelivr.net/gh/supermx1/nigerian-banks-api@main/data.json";
const NIGERIAN_BANK_LOGO_BASE =
  "https://cdn.jsdelivr.net/gh/supermx1/nigerian-banks-api@main/logos";

function NigeriaBankSelect({ value, bankCode, onChange }) {
  const [banks, setBanks] = React.useState([]);
  const [query, setQuery] = React.useState(value || "");
  const [open, setOpen] = React.useState(false);
  const [loadingBanks, setLoadingBanks] = React.useState(false);
  const [bankLoadError, setBankLoadError] = React.useState(false);

  React.useEffect(() => {
    setQuery(value || "");
  }, [value]);

  React.useEffect(() => {
    let alive = true;
    const loadBanks = async () => {
      try {
        setLoadingBanks(true);
        setBankLoadError(false);
        const response = await fetch(NIGERIAN_BANKS_API);
        if (!response.ok) throw new Error("Unable to load Nigerian banks");
        const raw = await response.json();
        const list = Array.isArray(raw) ? raw : raw?.banks || raw?.data || [];
        const normalized = list
          .map((bank) => {
            const name = bank?.name || bank?.Name || bank?.bank_name || bank?.bankName || "";
            const code = String(bank?.code || bank?.Code || bank?.bank_code || bank?.bankCode || "");
            const slug =
              bank?.slug ||
              bank?.Slug ||
              name
                .toLowerCase()
                .replace(/&/g, "and")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
            const logo =
              bank?.logo ||
              bank?.logo_url ||
              bank?.logoUrl ||
              (slug ? `${NIGERIAN_BANK_LOGO_BASE}/${slug}.png` : "");
            return { name, code, slug, logo };
          })
          .filter((bank) => bank.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (alive) setBanks(normalized);
      } catch (error) {
        if (alive) setBankLoadError(true);
      } finally {
        if (alive) setLoadingBanks(false);
      }
    };
    loadBanks();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = banks
    .filter((bank) => bank.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 80);

  const selected = banks.find(
    (bank) => bank.name === value || (bankCode && bank.code === bankCode),
  );

  return (
    <div className="edBankSelect">
      <button
        type="button"
        className="edBankSelectControl"
        onClick={() => setOpen((current) => !current)}
      >
        <BankLogo bank={selected} />
        <span>
          <strong>{value || "Select Nigerian bank"}</strong>
          <small>{bankCode ? `Bank code ${bankCode}` : "Search the Nigerian bank directory"}</small>
        </span>
        <span className="edBankSelectChevron">⌄</span>
      </button>

      {open && (
        <div className="edBankSelectMenu">
          <div className="edBankSearch">
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search bank..."
            />
          </div>

          <div className="edBankOptions">
            {loadingBanks && <div className="edBankStatus">Loading Nigerian banks…</div>}
            {bankLoadError && (
              <div className="edBankStatus error">
                Bank directory could not load. Check your internet connection and try again.
              </div>
            )}
            {!loadingBanks && !bankLoadError && filtered.length === 0 && (
              <div className="edBankStatus">No matching bank found.</div>
            )}
            {!loadingBanks &&
              filtered.map((bank) => (
                <button
                  type="button"
                  key={`${bank.code}-${bank.name}`}
                  onClick={() => {
                    onChange(bank);
                    setQuery(bank.name);
                    setOpen(false);
                  }}
                >
                  <BankLogo bank={bank} />
                  <span>
                    <strong>{bank.name}</strong>
                    <small>{bank.code ? `Code ${bank.code}` : "Nigeria"}</small>
                  </span>
                  {bank.name === value && <CheckCircleRounded />}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BankLogo({ bank }) {
  const [failed, setFailed] = React.useState(false);
  const initials = (bank?.name || "Bank")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  if (!bank?.logo || failed) {
    return <span className="edBankLogoFallback">{initials}</span>;
  }

  return (
    <span className="edBankLogo">
      <img src={bank.logo} alt="" onError={() => setFailed(true)} />
    </span>
  );
}

function Heading({ title, text }) {
  return <header className="edOnboardHeading"><h3>{title}</h3><p>{text}</p></header>;
}
function Grid({ children }) {
  return <div className="edOnboardGrid">{children}</div>;
}
function Field({ label, error, children }) {
  return <label className={`edOnboardField ${error ? "error" : ""}`}><span>{label}</span>{children}{error && <small>{error}</small>}</label>;
}
function PublicInfo({ activeStaff }) {
  return <><LocationOnRounded /><div><small>EXECUTIVE JURISDICTION</small><strong>{activeStaff?.territory?.regionName || "Assigned region"}</strong><span>{activeStaff?.territory?.totalStates || 0} states available for assignment</span></div></>;
}
